import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import FullPagePaymentWall, { PaymentWallConfirmation, PaymentWallPreflight } from '../components/FullPagePaymentWall';
import { uploadToR2 } from '../utils/r2Upload';
import { runPayNowFlow } from '../utils/payNowFlow';
import RendersPanel, { RenderJob } from '../components/clip-editor/RendersPanel';
import RightPanel from '../components/story/RightPanel';
import {
  AspectRatio,
  AudioFile,
  CaptionAlign,
  DurationMode,
  KenBurnsEffect,
  KenBurnsStep,
  LibraryAsset,
  CaptionOverlay,
  MotionPreset,
  SlideSourceType,
  SlideState,
  SlideTransition,
  Story,
  TextStyleFields,
} from '../components/story/types';
import { ASPECT_RATIOS, DEFAULT_CAPTION_BOX, DEFAULT_NARRATION_BOX, KB_EFFECTS, VIDEO_ACCEPT } from '../components/story/constants';
import {
  clampOverlayPosition,
  clampOverlaySize,
  estimateCaptionBox,
  estimateNarrationBox,
  normalizeCaptionOverlay,
  normalizeNarrationOverlay,
  measureOverlayLayout,
} from '../components/story/overlays';
import { textStyleToCss } from '../components/story/textStyle';

export type { SlideState };

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface PendingImageVideoAction {
  projectId: string;
  videoId?: string;
  imageProjectId?: string;
}

interface PaymentWallState {
  preflight: PaymentWallPreflight;
  action: PendingImageVideoAction;
}

function isPaymentRequiredPayload(payload: unknown): payload is PaymentWallPreflight {
  if (!payload || typeof payload !== 'object') return false;
  const obj = payload as Record<string, unknown>;
  return obj.status === 'payment_required'
    && typeof obj.featureKey === 'string'
    && typeof obj.units === 'number'
    && typeof obj.includedUnitsRemaining === 'number'
    && typeof obj.paygOption === 'object'
    && obj.paygOption !== null
    && typeof (obj.paygOption as Record<string, unknown>).unitPrice === 'number'
    && typeof (obj.paygOption as Record<string, unknown>).estimatedAmount === 'number'
    && typeof obj.plansOption === 'object'
    && obj.plansOption !== null
    && Array.isArray((obj.plansOption as Record<string, unknown>).recommendedPlanIds);
}

let clientIdSeq = 0;
function newClientId(): string {
  clientIdSeq += 1;
  return `c${Date.now().toString(36)}-${clientIdSeq}`;
}

function createDefaultKBSteps(): KenBurnsStep[] {
  return KB_EFFECTS.map(e => ({
    effect: e.value,
    duration: 3,
    enabled: e.value === 'zoom_in',
  }));
}

/** Rebuild the full step list from the subset a saved Story persisted. */
function hydrateKBSteps(saved: { effect: KenBurnsEffect; duration: number }[]): KenBurnsStep[] {
  if (!saved || saved.length === 0) return createDefaultKBSteps();
  return KB_EFFECTS.map(e => {
    const match = saved.find(k => k.effect === e.value);
    return { effect: e.value, duration: match?.duration ?? 3, enabled: !!match };
  });
}

const PRESET_EFFECTS: Record<MotionPreset, KenBurnsEffect[]> = {
  documentary: ['slow_zoom'],
  dynamic: ['zoom_in', 'pan_left', 'pan_right', 'zoom_out'],
  cinematic: ['zoom_in', 'pan_left'],
  custom: [],
};

function getPreviewMotion(slide: SlideState, slideIndex: number): KenBurnsEffect {
  if (slide.motionPreset === 'custom') {
    const enabled = slide.kenBurnsSteps.filter(s => s.enabled);
    if (enabled.length === 0) return 'none';
    return enabled[slideIndex % enabled.length].effect;
  }
  const effects = PRESET_EFFECTS[slide.motionPreset] || ['none'];
  if (effects.length === 0) return 'none';
  return effects[slideIndex % effects.length];
}

const ImageVideoUpload: React.FC = () => {
  const { id, storyId: routeStoryId } = useParams<{ id: string; storyId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [projectName, setProjectName] = useState<string | null>(null);
  const [story, setStory] = useState<Story | null>(null);
  const [loadingStory, setLoadingStory] = useState(!!routeStoryId);
  const [editingName, setEditingName] = useState(!routeStoryId);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [openingClip, setOpeningClip] = useState(false);
  const [audioLibrary, setAudioLibrary] = useState<LibraryAsset[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(true);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [slides, setSlides] = useState<SlideState[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [platform, setPlatform] = useState('youtube_shorts');
  const [audience, setAudience] = useState('general');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // The story's own renders, listed and polled by the shared RendersPanel. `watchingJob` is the
  // finished render being played in the preview area, in place of the slide.
  const [renderJobs, setRenderJobs] = useState<RenderJob[]>([]);
  const [watchingJob, setWatchingJob] = useState<RenderJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentWall, setPaymentWall] = useState<PaymentWallState | null>(null);
  const [processingPaymentWall, setProcessingPaymentWall] = useState(false);
  const pendingChargeAuthorizationIdRef = useRef<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewSlideIndex, setPreviewSlideIndex] = useState(0);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const foregroundAudioInputRef = useRef<HTMLInputElement>(null);
  const backgroundAudioInputRef = useRef<HTMLInputElement>(null);
  const filmstripRef = useRef<HTMLDivElement>(null);
  const clipVideoRef = useRef<HTMLVideoElement>(null);

  const activeSlide = slides[activeIndex] || null;
  const clipSlideCount = slides.filter(sl => sl.sourceType === 'video').length;
  
  // The shared audio library backing both pickers.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/assets?assetType=audio`, { credentials: 'include' });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setAudioLibrary(data.assets || []);
        }
      } catch { /* the upload button still works without the library */ }
      if (!cancelled) setLoadingLibrary(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch project name for the breadcrumb
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${id}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setProjectName(data.name);
        }
      } catch { /* ignore */ }
    };
    if (id) fetchProject();
  }, [id]);

  // ── Story persistence ──────────────────────────────────────
  // Slides live on the server, so a Story can be closed and reopened. Assets are pushed to R2
  // as they are added; everything else is autosaved on a debounce.

  const buildSlidePayload = useCallback((list: SlideState[]) => list.map((s, i) => ({
    index: i,
    fileName: s.fileName,
    fileSize: s.fileSize,
    imageR2Key: s.r2Key || null,
    sourceType: s.sourceType,
    shortId: s.shortId || null,
    posterR2Key: s.posterR2Key || null,
    sourceDuration: s.sourceDuration ?? null,
    trimIn: s.trimIn ?? null,
    trimOut: s.trimOut ?? null,
    playbackSpeed: s.playbackSpeed,
    sourceMuted: s.sourceMuted,
    sourceVolume: s.sourceVolume,
    caption: s.caption,
    emojis: s.emojis,
    emojiOverlays: s.emojiOverlays,
    captionOverlay: s.captionOverlay,
    narrationOverlay: s.narrationOverlay,
    aspectRatio: s.aspectRatio,
    longDescriptionText: s.longDescriptionText,
    ttsEnabled: s.ttsEnabled,
    showLongTextWithTts: s.showLongTextWithTts,
    backgroundVolume: s.backgroundVolume,
    foregroundGap: s.foregroundGap,
    matchAudioDuration: s.matchAudioDuration,
    motionPreset: s.motionPreset,
    // Custom with no step ticked previews as a still, so it is sent as one. An empty list
    // would make the server fall back to a random effect and the render would move.
    kenBurnsEffects: s.motionPreset === 'custom'
      ? (s.kenBurnsSteps.some(k => k.enabled)
          ? s.kenBurnsSteps.filter(k => k.enabled).map(k => ({ effect: k.effect, duration: k.duration }))
          : [{ effect: 'none' as KenBurnsEffect, duration: s.fixedDuration }])
      : [],
    durationMode: s.durationMode,
    fixedDuration: s.fixedDuration,
    transition: s.transition,
    transitionOffset: s.transitionOffset,
    transitionDuration: s.transitionDuration,
    audios: (s.audioFiles || []).map(af => ({
      fileName: af.fileName,
      fileSize: af.fileSize,
      r2Key: af.r2Key || null,
      mode: af.mode,
    })),
  })), []);

  // Hydrate a reopened Story.
  useEffect(() => {
    if (!routeStoryId) { setLoadingStory(false); return; }
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/image-projects/${routeStoryId}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Story not found');
        const data = await res.json();
        if (cancelled) return;

        setStory({ id: data.id, name: data.name, status: data.status, videoId: data.videoId });
        if (data.platform) setPlatform(data.platform);
        if (data.audience) setAudience(data.audience);
        setDescription(data.description || '');
        setSlides((data.slides || []).map((sl: any) => ({
          clientId: newClientId(),
          file: null,
          fileName: sl.fileName || '',
          fileSize: sl.fileSize || 0,
          r2Key: sl.imageR2Key || undefined,
          preview: sl.imageUrl || '',
          caption: sl.caption || '',
          emojis: sl.emojis || [],
          emojiOverlays: sl.emojiOverlays || [],
          captionOverlay: sl.captionOverlay || null,
          narrationOverlay: sl.narrationOverlay || null,
          aspectRatio: (sl.aspectRatio || '9:16') as AspectRatio,
          longDescriptionText: sl.longDescriptionText || '',
          ttsEnabled: !!sl.ttsEnabled,
          showLongTextWithTts: !!sl.showLongTextWithTts,
          audioFiles: (sl.audios || []).map((a: any) => ({
            clientId: newClientId(),
            file: null,
            fileName: a.fileName || '',
            fileSize: a.fileSize || 0,
            r2Key: a.r2Key || undefined,
            mode: (a.mode || 'foreground') as 'foreground' | 'background',
          })),
          backgroundVolume: sl.backgroundVolume ?? 30,
          foregroundGap: sl.foregroundGap ?? 2.0,
          matchAudioDuration: !!sl.matchAudioDuration,
          motionPreset: (sl.motionPreset || 'documentary') as MotionPreset,
          kenBurnsSteps: hydrateKBSteps(sl.kenBurnsEffects || []),
          durationMode: (sl.durationMode || 'fixed') as DurationMode,
          // For a clip, the Clip Editor's trim is the source of truth for length.
          fixedDuration: sl.clipDuration ?? sl.fixedDuration ?? 5,
          transition: (sl.transition || 'fade') as SlideTransition,
          transitionOffset: sl.transitionOffset ?? undefined,
          transitionDuration: sl.transitionDuration ?? undefined,
          sourceType: (sl.sourceType === 'video' ? 'video' : 'image') as SlideSourceType,
          shortId: sl.shortId || undefined,
          posterR2Key: sl.posterR2Key || undefined,
          posterUrl: sl.posterUrl || undefined,
          sourceDuration: sl.sourceDuration ?? undefined,
          trimIn: sl.trimIn ?? undefined,
          trimOut: sl.trimOut ?? undefined,
          playbackSpeed: sl.playbackSpeed ?? 1,
          sourceMuted: !!sl.sourceMuted,
          sourceVolume: sl.sourceVolume ?? 100,
        })));
        setActiveIndex(0);
      } catch {
        if (!cancelled) setError('This story could not be loaded.');
      } finally {
        if (!cancelled) setLoadingStory(false);
      }
    })();

    return () => { cancelled = true; };
  }, [routeStoryId]);

  // Debounced autosave. Skipped until the Story exists and its slides have been hydrated,
  // so an in-flight load can never overwrite the saved set with an empty one.
  const hydratedRef = useRef(false);
  useEffect(() => { if (!loadingStory) hydratedRef.current = true; }, [loadingStory]);

  const persistStory = useCallback(async (list: SlideState[]) => {
    if (!story) return false;
    setSaveState('saving');
    try {
      const res = await fetch(`${API_BASE}/api/image-projects/${story.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          slides: buildSlidePayload(list),
          platform,
          audience,
          description,
        }),
      });
      setSaveState(res.ok ? 'saved' : 'idle');
      return res.ok;
    } catch {
      setSaveState('idle');
      return false;
    }
  }, [story, buildSlidePayload, platform, audience, description]);

  useEffect(() => {
    if (!story || loadingStory || !hydratedRef.current) return;
    // Slide rows are replaced wholesale on save; doing that mid-render would pull them out
    // from under the worker, which writes TTS paths back onto them.
    if (story.status === 'processing') return;
    // Wait for in-flight uploads so slides are never persisted without their R2 key.
    const uploadPending = slides.some(s =>
      s.uploading || s.audioFiles.some(af => af.file && !af.r2Key));
    if (uploadPending) return;

    setSaveState('saving');
    const timer = setTimeout(() => { persistStory(slides); }, 800);
    return () => clearTimeout(timer);
  }, [slides, platform, audience, description, story, loadingStory, persistStory]);

  // Upload newly added files straight to R2 under the Story's prefix.
  const requestStoryUploadUrls = useCallback(async (
    assets: { clientId: string; kind: 'image' | 'video' | 'audio'; fileName: string }[],
  ): Promise<Record<string, { r2Key: string; url: string }>> => {
    if (!story || assets.length === 0) return {};
    const res = await fetch(`${API_BASE}/api/image-projects/${story.id}/assets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ assets }),
    });
    if (!res.ok) throw new Error('Failed to prepare upload');
    const { uploads } = await res.json();
    const map: Record<string, { r2Key: string; url: string }> = {};
    for (const u of uploads) map[u.clientId] = { r2Key: u.r2Key, url: u.url };
    return map;
  }, [story]);

  const saveName = async () => {
    const trimmed = draftName.trim();
    if (!trimmed || !id) return;
    setSavingName(true);
    setError(null);
    try {
      const res = story
        ? await fetch(`${API_BASE}/api/image-projects/${story.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name: trimmed }),
          })
        : await fetch(`${API_BASE}/api/projects/${id}/image-video`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name: trimmed }),
          });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save story name');
      }
      const data = await res.json();
      if (story) {
        setStory({ ...story, name: data.name });
      } else {
        setStory({ id: data.id, name: data.name, status: data.status, videoId: data.videoId });
        hydratedRef.current = true;
        navigate(`/projects/${id}/image-video/${data.id}`, { replace: true });
      }
      setEditingName(false);
    } catch (e: any) {
      setError(e.message || 'Failed to save name');
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    if (editingName) {
      setDraftName(story?.name || searchParams.get('name') || 'Untitled');
      requestAnimationFrame(() => nameInputRef.current?.focus());
    }
  }, [editingName, story, searchParams]);


  // Slideshow preview auto-advance
  useEffect(() => {
    if (!previewing || slides.length === 0) return;
    const duration = (slides[previewSlideIndex]?.fixedDuration || 5) * 1000;
    previewTimerRef.current = setTimeout(() => {
      setPreviewSlideIndex(prev => {
        if (prev >= slides.length - 1) {
          setPreviewing(false);
          return 0;
        }
        return prev + 1;
      });
    }, duration);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [previewing, previewSlideIndex, slides]);

  const startPreview = () => {
    setPreviewSlideIndex(0);
    setPreviewing(true);
  };

  const stopPreview = () => {
    setPreviewing(false);
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
  };

  const getSlideAudioState = (slide: SlideState) => {
    const hasNarration = slide.ttsEnabled && slide.longDescriptionText.trim().length > 0;
    const foregroundCount = slide.audioFiles.filter(af => af.mode === 'foreground').length;
    const backgroundCount = slide.audioFiles.filter(af => af.mode === 'background').length;

    return {
      hasNarration,
      hasForeground: foregroundCount > 0,
      hasBackground: backgroundCount > 0,
    };
  };

  const resolveDurationMode = (slide: SlideState): DurationMode => {
    const { hasNarration, hasForeground, hasBackground } = getSlideAudioState(slide);

    if (hasNarration) return 'narration';
    if (hasForeground && hasBackground) return 'audio';
    if (hasForeground) return slide.matchAudioDuration ? 'audio' : 'fixed';
    return 'fixed';
  };

  /**
   * Measures the caption against the same font the preview draws with, so the renderer can burn
   * the exact lines instead of estimating from an average glyph width. An auto plate wraps at a
   * readable width and grows; a plate the author sized is a hard limit the font shrinks into.
   */
  const withMeasuredCaptionLayout = (
    overlay: CaptionOverlay | null, fw: number, fh: number,
  ): CaptionOverlay | null => {
    if (!overlay || !overlay.text?.trim() || fw <= 0 || fh <= 0) return overlay;
    const manual = overlay.bgAuto === false;
    const measured = measureOverlayLayout(
      overlay.text, overlay, { width: fw, height: fh },
      manual ? overlay.bgWidth : null,
      manual ? overlay.bgHeight : null,
    );
    return measured ? { ...overlay, ...measured } : overlay;
  };

  const normalizeSlideState = (slide: SlideState): SlideState => {
    const hasNarration = slide.ttsEnabled && slide.longDescriptionText.trim().length > 0;
    const matchAudioDuration = hasNarration ? false : slide.matchAudioDuration;
    const normalizedCaptionOverlay = withMeasuredCaptionLayout(
      normalizeCaptionOverlay(slide.captionOverlay), frameWidth, frameHeight);
    const normalizedNarrationOverlay = normalizeNarrationOverlay(slide.narrationOverlay, slide.longDescriptionText);

    return {
      ...slide,
      matchAudioDuration,
      captionOverlay: normalizedCaptionOverlay,
      narrationOverlay: normalizedNarrationOverlay,
      durationMode: resolveDurationMode({ ...slide, matchAudioDuration }),
    };
  };

  useEffect(() => {
    setSlides(prev => {
      let hasChanges = false;
      const normalized = prev.map(slide => {
        const next = normalizeSlideState(slide);
        const changed =
          slide.captionOverlay?.bgHeight !== next.captionOverlay?.bgHeight ||
          slide.captionOverlay?.bgWidth !== next.captionOverlay?.bgWidth ||
          slide.captionOverlay?.x !== next.captionOverlay?.x ||
          slide.captionOverlay?.y !== next.captionOverlay?.y ||
          slide.narrationOverlay?.bgHeight !== next.narrationOverlay?.bgHeight ||
          slide.narrationOverlay?.bgWidth !== next.narrationOverlay?.bgWidth ||
          slide.narrationOverlay?.x !== next.narrationOverlay?.x ||
          slide.narrationOverlay?.y !== next.narrationOverlay?.y ||
          slide.matchAudioDuration !== next.matchAudioDuration ||
          slide.durationMode !== next.durationMode;

        if (changed) hasChanges = true;
        return changed ? next : slide;
      });

      return hasChanges ? normalized : prev;
    });
  }, [slides]);

  /** Push a just-added file to R2 and stamp its key onto the matching slide/audio. */
  const uploadSlideAsset = useCallback(async (
    clientId: string,
    kind: 'image' | 'video' | 'audio',
    file: File,
  ) => {
    try {
      const urls = await requestStoryUploadUrls([{ clientId, kind, fileName: file.name }]);
      const target = urls[clientId];
      if (!target) throw new Error('No upload URL');
      await uploadToR2(target.url, file);
      setSlides(prev => prev.map(sl => {
        if (kind === 'image' || kind === 'video') {
          return sl.clientId === clientId ? { ...sl, r2Key: target.r2Key, uploading: false } : sl;
        }
        if (!sl.audioFiles.some(af => af.clientId === clientId)) return sl;
        return {
          ...sl,
          audioFiles: sl.audioFiles.map(af =>
            af.clientId === clientId ? { ...af, r2Key: target.r2Key } : af),
        };
      }));
    } catch {
      setError('Upload failed — please remove and re-add the file.');
      setSlides(prev => prev.map(sl => sl.clientId === clientId ? { ...sl, uploading: false } : sl));
    }
  }, [requestStoryUploadUrls]);

  /**
   * Read a just-added clip's length and first frame in the browser. The render still needs a
   * server-side poster, but this is what makes the slide usable the moment it is dropped in.
   */
  const probeVideoSlide = useCallback((clientId: string, objectUrl: string) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.src = objectUrl;

    const fail = () => {
      setSlides(prev => prev.map(sl =>
        sl.clientId === clientId ? { ...sl, sourceDuration: undefined } : sl));
    };

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : undefined;
      setSlides(prev => prev.map(sl => sl.clientId === clientId
        ? {
            ...sl,
            sourceDuration: duration,
            trimIn: sl.trimIn ?? 0,
            trimOut: sl.trimOut ?? duration,
            fixedDuration: duration ? Math.max(1, Math.round(duration)) : sl.fixedDuration,
          }
        : sl));
      // Seeking a hair past zero avoids the black leader some encoders start with.
      video.currentTime = Math.min(0.1, (duration || 1) / 2);
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx || !canvas.width || !canvas.height) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const posterUrl = canvas.toDataURL('image/jpeg', 0.7);
        setSlides(prev => prev.map(sl =>
          sl.clientId === clientId ? { ...sl, posterUrl } : sl));
      } catch { /* a tainted or undecodable frame just leaves the poster empty */ }
    };

    video.onerror = fail;
  }, []);

  const handleSlideFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newSlides: SlideState[] = files.map(file => ({
      clientId: newClientId(),
      file,
      fileName: file.name,
      fileSize: file.size,
      uploading: true,
      preview: URL.createObjectURL(file),
      sourceType: (file.type.startsWith('video/') ? 'video' : 'image') as SlideSourceType,
      playbackSpeed: 1,
      sourceMuted: false,
      sourceVolume: 100,
      caption: '',
      emojis: [],
      emojiOverlays: [],
      captionOverlay: null,
      narrationOverlay: null,
      aspectRatio: '9:16' as AspectRatio,
      longDescriptionText: '',
      ttsEnabled: false,
      showLongTextWithTts: false,
      audioFiles: [],
      backgroundVolume: 30,
      foregroundGap: 2.0,
      matchAudioDuration: false,
      motionPreset: 'documentary' as MotionPreset,
      kenBurnsSteps: createDefaultKBSteps(),
      durationMode: 'fixed' as DurationMode,
      fixedDuration: 5,
      transition: 'fade' as SlideTransition,
      transitionOffset: undefined,
      transitionDuration: undefined,
    }));
    setSlides(prev => {
      // The ratio is global: a slide added later takes whatever the story is already set to.
      const ratio = prev[0]?.aspectRatio;
      const updated = [...prev, ...(ratio ? newSlides.map(sl => ({ ...sl, aspectRatio: ratio })) : newSlides)];
      if (prev.length === 0) setActiveIndex(0);
      return updated;
    });
    newSlides.forEach(sl => {
      if (!sl.file) return;
      uploadSlideAsset(sl.clientId, sl.sourceType === 'video' ? 'video' : 'image', sl.file);
      // A clip needs its length and a still before the Motion tab and filmstrip can show anything.
      if (sl.sourceType === 'video') probeVideoSlide(sl.clientId, sl.preview);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [uploadSlideAsset, probeVideoSlide]);

  const updateActive = useCallback((patch: Partial<SlideState>) => {
    setSlides(prev => prev.map((s, i) => i === activeIndex ? normalizeSlideState({ ...s, ...patch }) : s));
  }, [activeIndex]);

  const removeSlide = useCallback((idx: number) => {
    setSlides(prev => {
      if (prev[idx].file) URL.revokeObjectURL(prev[idx].preview);
      const next = prev.filter((_, i) => i !== idx);
      if (activeIndex >= next.length) setActiveIndex(Math.max(0, next.length - 1));
      return next;
    });
  }, [activeIndex]);

  const scrollFilmstrip = (dir: 'left' | 'right') => {
    filmstripRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  // ── Filmstrip drag-to-reorder ──
  const dragSlideRef = useRef<{ fromIndex: number } | null>(null);

  const handleThumbDragStart = (e: React.DragEvent, idx: number) => {
    dragSlideRef.current = { fromIndex: idx };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
    (e.currentTarget as HTMLElement).classList.add('se-thumb-dragging');
  };

  const handleThumbDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleThumbDragEnter = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).classList.add('se-thumb-dragover');
  };

  const handleThumbDragLeave = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).classList.remove('se-thumb-dragover');
  };

  const handleThumbDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove('se-thumb-dragover');
    if (!dragSlideRef.current) return;
    const fromIndex = dragSlideRef.current.fromIndex;
    if (fromIndex === toIndex) return;
    setSlides(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    // Keep selection on the moved slide
    setActiveIndex(toIndex);
    dragSlideRef.current = null;
  };

  const handleThumbDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).classList.remove('se-thumb-dragging');
    dragSlideRef.current = null;
  };

  // Aspect ratio is global — applies to ALL slides
  const globalAspectRatio = slides[0]?.aspectRatio || '9:16';
  const setGlobalAspectRatio = (ratio: AspectRatio) => {
    setSlides(prev => prev.map(s => ({ ...s, aspectRatio: ratio })));
  };

  /**
   * Hand off to the Clip Editor, which owns the clip. The return target brings the author back
   * to this Story on the same slide.
   */
  const openClipEditor = async () => {
    if (!activeSlide || activeSlide.sourceType !== 'video' || !story) return;
    if (activeSlide.uploading || !activeSlide.r2Key) {
      setError('This clip is still uploading — try again in a moment.');
      return;
    }

    setOpeningClip(true);
    setError(null);
    try {
      let shortId = activeSlide.shortId;

      if (!shortId) {
        // The hand-off looks the slide up by index server-side, so flush first — a pending
        // debounce would otherwise resolve against a stale slide set.
        const saved = await persistStory(slides);
        if (!saved) throw new Error('Could not save the story before opening the Clip Editor');

        const res = await fetch(
          `${API_BASE}/api/image-projects/${story.id}/slides/${activeIndex}/clip`,
          { method: 'POST', credentials: 'include' },
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Could not open this clip in the Clip Editor');
        }
        shortId = (await res.json()).shortId as string;

        // Hold the link in state: the next autosave rewrites every slide row, and would drop
        // the short the server just attached.
        setSlides(prev => prev.map((sl, i) => i === activeIndex ? { ...sl, shortId } : sl));
      }

      const back = `/projects/${id}/image-video/${story.id}`;
      // storyId lets the Clip Editor name the Story on its breadcrumb and its Done button.
      navigate(`/clips/${shortId}?returnTo=${encodeURIComponent(back)}&slide=${activeIndex}&storyId=${story.id}`);
    } catch (e: any) {
      setError(e.message || 'Could not open the Clip Editor');
    } finally {
      setOpeningClip(false);
    }
  };

  const toggleEmoji = (emoji: string) => {
    if (!activeSlide) return;
    const emojis = activeSlide.emojis.includes(emoji)
      ? activeSlide.emojis.filter(e => e !== emoji)
      : [...activeSlide.emojis, emoji];
    updateActive({ emojis });
  };

  const toggleKBEffect = (effect: KenBurnsEffect) => {
    if (!activeSlide) return;
    const steps = activeSlide.kenBurnsSteps.map(s =>
      s.effect === effect ? { ...s, enabled: !s.enabled } : s
    );
    updateActive({ kenBurnsSteps: steps });
  };

  const updateKBDuration = (effect: KenBurnsEffect, duration: number) => {
    if (!activeSlide) return;
    const steps = activeSlide.kenBurnsSteps.map(s =>
      s.effect === effect ? { ...s, duration } : s
    );
    updateActive({ kenBurnsSteps: steps });
  };

  const addForegroundAudio = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!activeSlide || files.length === 0) return;
    
    const newAudioFiles: AudioFile[] = files.map(file => ({
      clientId: newClientId(),
      file,
      fileName: file.name,
      fileSize: file.size,
      mode: 'foreground' as 'foreground' | 'background',
    }));
    
    updateActive({ audioFiles: [...activeSlide.audioFiles, ...newAudioFiles] });
    newAudioFiles.forEach(af => { if (af.file) uploadSlideAsset(af.clientId, 'audio', af.file); });
    if (foregroundAudioInputRef.current) foregroundAudioInputRef.current.value = '';
  };

  const addBackgroundAudio = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!activeSlide || files.length === 0) return;
    
    const newAudioFiles: AudioFile[] = files.map(file => ({
      clientId: newClientId(),
      file,
      fileName: file.name,
      fileSize: file.size,
      mode: 'background' as 'foreground' | 'background',
    }));
    
    updateActive({ audioFiles: [...activeSlide.audioFiles, ...newAudioFiles] });
    newAudioFiles.forEach(af => { if (af.file) uploadSlideAsset(af.clientId, 'audio', af.file); });
    if (backgroundAudioInputRef.current) backgroundAudioInputRef.current.value = '';
  };

  const addLibraryAudio = (asset: LibraryAsset, mode: 'foreground' | 'background') => {
    if (!activeSlide) return;
    updateActive({
      audioFiles: [...activeSlide.audioFiles, {
        clientId: newClientId(),
        file: null,          // nothing to upload — the asset is already in R2
        fileName: asset.name,
        fileSize: 0,
        r2Key: asset.r2Key,
        mode,
      }],
    });
  };

  const removeAudio = (audioIdx: number) => {
    if (!activeSlide) return;
    updateActive({ audioFiles: activeSlide.audioFiles.filter((_, i) => i !== audioIdx) });
  };

  const updateNarrationText = (longDescriptionText: string) => {
    if (!activeSlide) return;

    // Auto-enable TTS when text is entered
    const hasNarrationText = longDescriptionText.trim().length > 0;
    const ttsEnabled = hasNarrationText;

    // Grow/shrink the narration box with the text, but only while it hasn't been
    // manually resized (dragging the resize handle opts it out of auto-sizing).
    let narrationOverlay = activeSlide.narrationOverlay;
    if (narrationOverlay) {
      const prevEstimate = estimateNarrationBox(activeSlide.longDescriptionText);
      const isAutoSized = Math.abs(narrationOverlay.bgWidth - prevEstimate.bgWidth) < 0.01
        && Math.abs(narrationOverlay.bgHeight - prevEstimate.bgHeight) < 0.01;
      if (isAutoSized) {
        const nextEstimate = estimateNarrationBox(longDescriptionText);
        const clamped = clampOverlayPosition(narrationOverlay.x, narrationOverlay.y, narrationOverlay.align, nextEstimate.bgWidth, nextEstimate.bgHeight);
        narrationOverlay = { ...narrationOverlay, ...nextEstimate, ...clamped };
      }
    }

    updateActive({
      longDescriptionText,
      ttsEnabled,
      showLongTextWithTts: hasNarrationText ? activeSlide.showLongTextWithTts : false,
      narrationOverlay,
    });
  };

  const isDurationFixed = () => {
    if (!activeSlide) return false;

    return resolveDurationMode(activeSlide) === 'fixed';
  };

  // ── Emoji overlay management ──
  const addEmojiOverlay = (emoji: string) => {
    if (!activeSlide) return;
    const exists = activeSlide.emojiOverlays.find(e => e.emoji === emoji);
    if (exists) {
      // Remove it
      updateActive({ emojiOverlays: activeSlide.emojiOverlays.filter(e => e.emoji !== emoji) });
    } else {
      // Add at a staggered default position
      const count = activeSlide.emojiOverlays.length;
      updateActive({
        emojiOverlays: [...activeSlide.emojiOverlays, { emoji, x: 10 + (count % 4) * 20, y: 5 + (count % 3) * 10 }],
      });
    }
  };

  const removeEmojiOverlay = (emoji: string) => {
    if (!activeSlide) return;
    updateActive({ emojiOverlays: activeSlide.emojiOverlays.filter(e => e.emoji !== emoji) });
  };

  const updateEmojiPos = (emoji: string, x: number, y: number) => {
    if (!activeSlide) return;
    updateActive({
      emojiOverlays: activeSlide.emojiOverlays.map(e =>
        e.emoji === emoji ? { ...e, x: Math.max(0, Math.min(95, x)), y: Math.max(0, Math.min(95, y)) } : e
      ),
    });
  };

  // ── Caption overlay management ──
  const setCaptionText = (text: string) => {
    if (!activeSlide) return;
    const current = activeSlide.captionOverlay;
    if (!current) {
      updateActive({
        caption: text,
        captionOverlay: text
          ? { text, x: 50, y: 82, align: 'center', ...estimateCaptionBox(text) }
          : null,
      });
      return;
    }
    // Grow/shrink the caption box with the text, but only while it hasn't been
    // manually resized (dragging the resize handle opts it out of auto-sizing).
    const prevWidth = estimateCaptionBox(activeSlide.caption).bgWidth;
    const isAutoWidth = Math.abs(current.bgWidth - prevWidth) < 0.01;
    if (!isAutoWidth) {
      updateActive({ caption: text, captionOverlay: { ...current, text } });
      return;
    }
    const { bgWidth } = estimateCaptionBox(text);
    const clamped = clampOverlayPosition(current.x, current.y, current.align, bgWidth, current.bgHeight);
    updateActive({
      caption: text,
      captionOverlay: { ...current, text, bgWidth, ...clamped },
    });
  };

  const setCaptionAlign = (align: CaptionAlign) => {
    if (!activeSlide?.captionOverlay) return;
    const { bgWidth, bgHeight } = clampOverlaySize(
      activeSlide.captionOverlay.bgWidth ?? DEFAULT_CAPTION_BOX.bgWidth,
      activeSlide.captionOverlay.bgHeight ?? DEFAULT_CAPTION_BOX.bgHeight,
    );
    const clamped = clampOverlayPosition(activeSlide.captionOverlay.x, activeSlide.captionOverlay.y, align, bgWidth, bgHeight);
    updateActive({
      captionOverlay: { ...activeSlide.captionOverlay, align, bgWidth, bgHeight, ...clamped },
    });
  };

  const updateCaptionPos = (x: number, y: number) => {
    if (!activeSlide?.captionOverlay) return;
    const { bgWidth, bgHeight } = clampOverlaySize(
      activeSlide.captionOverlay.bgWidth ?? DEFAULT_CAPTION_BOX.bgWidth,
      activeSlide.captionOverlay.bgHeight ?? DEFAULT_CAPTION_BOX.bgHeight,
    );
    const clamped = clampOverlayPosition(x, y, activeSlide.captionOverlay.align, bgWidth, bgHeight);
    updateActive({
      captionOverlay: { ...activeSlide.captionOverlay, bgWidth, bgHeight, ...clamped },
    });
  };

  const updateCaptionSize = (bgWidth: number, bgHeight: number) => {
    if (!activeSlide?.captionOverlay) return;
    const size = clampOverlaySize(bgWidth, bgHeight);
    const clamped = clampOverlayPosition(
      activeSlide.captionOverlay.x,
      activeSlide.captionOverlay.y,
      activeSlide.captionOverlay.align,
      size.bgWidth,
      size.bgHeight,
    );
    // Dragging the handle is the author taking control: from here the burn uses exactly this
    // rectangle, and the text is fitted inside it rather than the plate growing to the text.
    updateActive({
      captionOverlay: { ...activeSlide.captionOverlay, ...size, ...clamped, bgAuto: false },
    });
  };

  const clearCaption = () => {
    updateActive({ caption: '', captionOverlay: null });
  };

  useEffect(() => {
    const el = previewRef.current;
    if (!el) { setFrameHeight(0); setFrameWidth(0); return; }
    const measure = () => { setFrameHeight(el.clientHeight); setFrameWidth(el.clientWidth); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [slides.length, activeIndex, previewing, globalAspectRatio]);

  const setCaptionStyle = (patch: Partial<TextStyleFields>) => {
    if (!activeSlide) return;
    const current = activeSlide.captionOverlay || {
      text: activeSlide.caption,
      x: 50,
      y: 82,
      align: 'center' as CaptionAlign,
      ...estimateCaptionBox(activeSlide.caption),
    };
    updateActive({ captionOverlay: { ...current, ...patch } });
  };

  // ── Narration overlay management ──
  const setNarrationAlign = (align: CaptionAlign) => {
    if (!activeSlide) return;
    const estimated = estimateNarrationBox(activeSlide.longDescriptionText);
    const current = activeSlide.narrationOverlay || {
      x: 50,
      y: 68,
      align: 'center' as CaptionAlign,
      bgWidth: estimated.bgWidth,
      bgHeight: estimated.bgHeight,
    };
    const size = clampOverlaySize(
      current.bgWidth ?? DEFAULT_NARRATION_BOX.bgWidth,
      current.bgHeight ?? DEFAULT_NARRATION_BOX.bgHeight,
    );
    const clamped = clampOverlayPosition(current.x, current.y, align, size.bgWidth, size.bgHeight);
    updateActive({ narrationOverlay: { ...current, ...size, align, ...clamped } });
  };

  const updateNarrationPos = (x: number, y: number) => {
    if (!activeSlide) return;
    const estimated = estimateNarrationBox(activeSlide.longDescriptionText);
    const current = activeSlide.narrationOverlay || {
      x: 50,
      y: 68,
      align: 'center' as CaptionAlign,
      bgWidth: estimated.bgWidth,
      bgHeight: estimated.bgHeight,
    };
    const size = clampOverlaySize(
      current.bgWidth ?? DEFAULT_NARRATION_BOX.bgWidth,
      current.bgHeight ?? DEFAULT_NARRATION_BOX.bgHeight,
    );
    const clamped = clampOverlayPosition(x, y, current.align, size.bgWidth, size.bgHeight);
    updateActive({
      narrationOverlay: {
        ...current,
        ...size,
        ...clamped,
      },
    });
  };

  const updateNarrationSize = (bgWidth: number, bgHeight: number) => {
    if (!activeSlide) return;
    const estimated = estimateNarrationBox(activeSlide.longDescriptionText);
    const current = activeSlide.narrationOverlay || {
      x: 50,
      y: 68,
      align: 'center' as CaptionAlign,
      bgWidth: estimated.bgWidth,
      bgHeight: estimated.bgHeight,
    };
    const size = clampOverlaySize(bgWidth, bgHeight);
    const clamped = clampOverlayPosition(current.x, current.y, current.align, size.bgWidth, size.bgHeight);
    updateActive({ narrationOverlay: { ...current, ...size, ...clamped } });
  };

  const clearNarrationOverlay = () => {
    updateActive({ showLongTextWithTts: false });
  };

  const setNarrationStyle = (patch: Partial<TextStyleFields>) => {
    if (!activeSlide) return;
    const current = activeSlide.narrationOverlay || {
      x: 50,
      y: 68,
      align: 'center' as CaptionAlign,
      ...estimateNarrationBox(activeSlide.longDescriptionText),
    };
    updateActive({ narrationOverlay: { ...current, ...patch } });
  };

  // ── Drag logic ──
  const previewRef = useRef<HTMLDivElement>(null);
  // Text is sized as a % of frame height, so the preview needs the frame's live pixel height.
  const [frameHeight, setFrameHeight] = useState(0);
  // Width matters as much as height for measuring where a caption wraps.
  const [frameWidth, setFrameWidth] = useState(0);
  const dragRef = useRef<{ type: 'emoji' | 'caption' | 'narration'; id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);
  const resizeRef = useRef<{ type: 'caption' | 'narration'; startX: number; startY: number; origWidth: number; origHeight: number } | null>(null);

  const handleDragStart = (e: React.MouseEvent, type: 'emoji' | 'caption' | 'narration', id: string, origX: number, origY: number) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { type, id, startX: e.clientX, startY: e.clientY, origX, origY };
  };

  const handleResizeStart = (e: React.MouseEvent, type: 'caption' | 'narration', origWidth: number, origHeight: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { type, startX: e.clientX, startY: e.clientY, origWidth, origHeight };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!previewRef.current) return;

      if (resizeRef.current) {
        const rect = previewRef.current.getBoundingClientRect();
        const dx = ((e.clientX - resizeRef.current.startX) / rect.width) * 100;
        const dy = ((e.clientY - resizeRef.current.startY) / rect.height) * 100;
        const newW = resizeRef.current.origWidth + dx;
        const newH = resizeRef.current.origHeight + dy;

        if (resizeRef.current.type === 'caption') {
          updateCaptionSize(newW, newH);
        } else {
          updateNarrationSize(newW, newH);
        }
        return;
      }

      if (!dragRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragRef.current.startX) / rect.width) * 100;
      const dy = ((e.clientY - dragRef.current.startY) / rect.height) * 100;
      const newX = dragRef.current.origX + dx;
      const newY = dragRef.current.origY + dy;

      if (dragRef.current.type === 'emoji') {
        updateEmojiPos(dragRef.current.id, newX, newY);
      } else if (dragRef.current.type === 'caption') {
        updateCaptionPos(newX, newY);
      } else {
        updateNarrationPos(newX, newY);
      }
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      resizeRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  });

  const confirmImageVideo = useCallback(async (
    videoId: string,
    imageProjectId: string,
    chargeAuthorizationId?: string,
  ): Promise<'queued' | 'payment_required'> => {
    const confirmRes = await fetch(`${API_BASE}/api/projects/${id}/image-video/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        videoId,
        imageProjectId,
        aspectRatio: globalAspectRatio,
        ...(chargeAuthorizationId ? { chargeAuthorizationId } : {}),
      }),
    });

    if (confirmRes.status === 402) {
      const payload = await confirmRes.json().catch(() => null);
      if (isPaymentRequiredPayload(payload)) {
        setPaymentWall({
          preflight: payload,
          action: {
            projectId: id || '',
            videoId,
            imageProjectId,
          },
        });
        return 'payment_required';
      }
      throw new Error('Usage limit reached. Open billing to continue.');
    }

    if (!confirmRes.ok) {
      const data = await confirmRes.json().catch(() => ({ error: 'Confirm failed' }));
      throw new Error(data.error || 'Confirm failed');
    }

    return 'queued';
  }, [id, globalAspectRatio]);

  const finishSubmissionSuccess = useCallback(() => {
    // Only object URLs need revoking; a reopened Story previews straight from R2.
    slides.forEach((s) => { if (s.file) URL.revokeObjectURL(s.preview); });
    navigate(`/projects/${id}`);
  }, [id, navigate, slides]);

  const preflightSlideshowUsage = useCallback(async (): Promise<'allowed' | 'payment_required'> => {
    const preflightRes = await fetch(`${API_BASE}/api/me/pricing/preflight`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        featureKey: 'slideshow',
        units: 1,
        context: { projectId: id || '' },
      }),
    });

    if (preflightRes.status === 402) {
      const payload = await preflightRes.json().catch(() => null);
      if (isPaymentRequiredPayload(payload)) {
        setPaymentWall({
          preflight: payload,
          action: {
            projectId: id || '',
          },
        });
        return 'payment_required';
      }
      throw new Error('Usage limit reached. Open billing to continue.');
    }

    if (!preflightRes.ok) {
      const data = await preflightRes.json().catch(() => ({ error: 'Unable to validate usage limits' }));
      throw new Error(data.error || 'Unable to validate usage limits');
    }

    return 'allowed';
  }, [id]);

  const submitImageVideoFlow = useCallback(async (
    options: { chargeAuthorizationId?: string; skipPreflight?: boolean } = {}
  ): Promise<'queued' | 'payment_required'> => {
    if (!id) {
      throw new Error('Project not found');
    }

    const { chargeAuthorizationId, skipPreflight = false } = options;

    if (!skipPreflight) {
      const preflightStatus = await preflightSlideshowUsage();
      if (preflightStatus === 'payment_required') {
        return 'payment_required';
      }
    }

    const mediaName = searchParams.get('name');
    const mediaDescription = searchParams.get('description');

    // 1. Init — get presigned URLs for all images + audios
    const initRes = await fetch(`${API_BASE}/api/projects/${id}/image-video/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        imageProjectId: story?.id,
        slides: buildSlidePayload(slides),
        platform,
        audience,
        kenBurnsMode: 'random',
        name: story?.name || mediaName || undefined,
        description: mediaDescription || undefined,
      }),
    });

    if (initRes.status === 402) {
      const payload = await initRes.json().catch(() => null);
      if (isPaymentRequiredPayload(payload)) {
        setPaymentWall({
          preflight: payload,
          action: {
            projectId: id || '',
          },
        });
        return 'payment_required';
      }
      throw new Error('Usage limit reached. Open billing to continue.');
    }

    if (!initRes.ok) {
      const data = await initRes.json().catch(() => ({ error: 'Init failed' }));
      throw new Error(data.error || 'Init failed');
    }

    const { videoId, imageProjectId, presignedUrls } = await initRes.json();

    // 2. Upload anything not already in R2 (assets added before this session are skipped:
    //    the server returns a null URL for keys it is reusing).
    const uploads = presignedUrls.flatMap((slideUrls: any, i: number) => {
      const slide = slides[i];
      const pending: Promise<unknown>[] = [];
      if (slideUrls.imageUrl && slide.file) pending.push(uploadToR2(slideUrls.imageUrl, slide.file));
      slideUrls.audioUrls.forEach((au: any, ai: number) => {
        const audio = slide.audioFiles[ai];
        if (au.url && audio?.file) pending.push(uploadToR2(au.url, audio.file));
      });
      return pending;
    });
    await Promise.all(uploads);

    // 3. Confirm — trigger processing
    const confirmStatus = await confirmImageVideo(videoId, imageProjectId, chargeAuthorizationId);
    if (confirmStatus === 'payment_required') {
      return 'payment_required';
    }

    finishSubmissionSuccess();
    return 'queued';
  }, [audience, buildSlidePayload, confirmImageVideo, finishSubmissionSuccess, id, platform, preflightSlideshowUsage, searchParams, slides, story]);

  const handleSubmit = async () => {
    if (slides.length === 0) return;
    if (!id) {
      setError('Project not found');
      return;
    }

    setSubmitting(true);
    setError(null);
    setPaymentWall(null);
    pendingChargeAuthorizationIdRef.current = null;

    try {
      await submitImageVideoFlow();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const closePaymentWall = () => {
    if (processingPaymentWall) return;
    pendingChargeAuthorizationIdRef.current = null;
    setPaymentWall(null);
  };

  const handleShowPlans = () => {
    setError(null);
  };

  const handlePayNowAuthorization = async (): Promise<PaymentWallConfirmation> => {
    if (!paymentWall) {
      throw new Error('Payment wall context is unavailable.');
    }
    const wall = paymentWall;

    setProcessingPaymentWall(true);
    setError(null);

    try {
      const methodsRes = await fetch(`${API_BASE}/api/me/billing/payment-methods`, {
        credentials: 'include',
      });

      if (methodsRes.ok) {
        const methodsData = await methodsRes.json().catch(() => null) as { paymentMethods?: unknown[] } | null;
        const paymentMethods: unknown[] = Array.isArray(methodsData?.paymentMethods)
          ? (methodsData?.paymentMethods as unknown[])
          : [];
        if (paymentMethods.length === 0) {
          window.location.assign('/account/billing');
          throw new Error('No payment method found. Add a payment method to continue.');
        }
      }

      const chargeContext: Record<string, string> = {
        projectId: wall.action.projectId,
      };
      if (wall.action.videoId) {
        chargeContext.videoId = wall.action.videoId;
      }
      if (wall.action.imageProjectId) {
        chargeContext.imageProjectId = wall.action.imageProjectId;
      }

      const payment = await runPayNowFlow({
        featureKey: wall.preflight.featureKey,
        units: wall.preflight.units,
        context: chargeContext,
        pricingSnapshot: {
          unitPrice: wall.preflight.paygOption.unitPrice,
          currency: wall.preflight.paygOption.currency,
        },
        fallbackAmount: wall.preflight.paygOption.estimatedAmount,
        fallbackCurrency: wall.preflight.paygOption.currency,
      });

      pendingChargeAuthorizationIdRef.current = payment.chargeAuthorizationId;

      return {
        outcome: 'payment_authorized',
        amount: payment.amount,
        currency: payment.currency,
      };
    } catch (err: any) {
      if (typeof err?.message === 'string' && /payment method/i.test(err.message)) {
        window.location.assign('/account/billing');
      }
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  const handleSwitchPlanOnly = async (planId: string): Promise<PaymentWallConfirmation> => {
    if (!paymentWall) {
      throw new Error('Payment wall context is unavailable.');
    }

    setProcessingPaymentWall(true);
    setError(null);

    try {
      const modeRes = await fetch(`${API_BASE}/api/me/pricing/mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mode: 'subscription',
          planId,
        }),
      });

      if (!modeRes.ok) {
        const data = await modeRes.json().catch(() => ({ error: 'Failed to switch plan' }));
        throw new Error(data.error || 'Failed to switch plan');
      }

      const modeData = await modeRes.json().catch(() => null) as { assignedPlan?: { name?: string; priceMonthly?: number } } | null;

      return {
        outcome: 'plan_updated',
        planName: modeData?.assignedPlan?.name,
        planPriceMonthly: modeData?.assignedPlan?.priceMonthly,
      };
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  const handleResumeBlockedAction = async (): Promise<void> => {
    if (!paymentWall) return;
    const wall = paymentWall;

    setProcessingPaymentWall(true);
    setError(null);

    try {
      if (wall.action.videoId && wall.action.imageProjectId) {
        const confirmStatus = await confirmImageVideo(
          wall.action.videoId,
          wall.action.imageProjectId,
          pendingChargeAuthorizationIdRef.current || undefined,
        );
        if (confirmStatus === 'payment_required') {
          throw new Error('Payment confirmation is still pending. Please retry in a moment.');
        }

        pendingChargeAuthorizationIdRef.current = null;
        setPaymentWall(null);
        finishSubmissionSuccess();
        return;
      }

      const submitStatus = await submitImageVideoFlow({
        chargeAuthorizationId: pendingChargeAuthorizationIdRef.current || undefined,
        skipPreflight: Boolean(pendingChargeAuthorizationIdRef.current),
      });
      if (submitStatus === 'payment_required') {
        throw new Error('Payment confirmation is still pending. Please retry in a moment.');
      }

      pendingChargeAuthorizationIdRef.current = null;
      setPaymentWall(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  const enabledKBSteps = activeSlide?.kenBurnsSteps.filter(k => k.enabled) || [];
  const totalKBDuration = enabledKBSteps.reduce((s, k) => s + k.duration, 0);

  if (paymentWall) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content se-page">
          <Header />

          <div className="breadcrumb">
            <div className="breadcrumb-links">
              <BreadcrumbBack to={`/projects/${id}`} />
              <Link to="/projects">Projects</Link>
              <span className="breadcrumb-sep">›</span>
              <Link to={`/projects/${id}`}>{projectName || 'Project'}</Link>
              <span className="breadcrumb-sep">›</span>
              <span>{story?.name || 'Story'}</span>
            </div>
          </div>

          {error && <div className="error-banner">⚠️ {error}</div>}

          <FullPagePaymentWall
            preflight={paymentWall.preflight}
            busy={processingPaymentWall}
            onPayNow={handlePayNowAuthorization}
            onShowPlans={handleShowPlans}
            onSwitchPlan={handleSwitchPlanOnly}
            onResumeBlockedAction={handleResumeBlockedAction}
            onCancel={closePaymentWall}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className={`main-content se-page ${story || loadingStory ? '' : 'se-page--locked'}`}>
        <Header />

        {/* Until the story arrives there is nothing to name, add to, or generate. Without this the
            page renders its empty state — "Untitled" in the breadcrumb and "Name your Story" —
            which reads as a broken story rather than one still loading. */}
        {loadingStory && (
          <div className="se-loading">
            <div className="spinner" />
            <p>Loading story…</p>
          </div>
        )}

        <div className="breadcrumb" hidden={loadingStory}>
          <div className="breadcrumb-links">
            <BreadcrumbBack to={`/projects/${id}`} />
            <Link to="/projects">Projects</Link>
            <span className="breadcrumb-sep">›</span>
            <Link to={`/projects/${id}`}>{projectName || 'Project'}</Link>
            <span className="breadcrumb-sep">›</span>
            {editingName ? (
              <span className="kc-name-edit">
                <input
                  ref={nameInputRef}
                  value={draftName}
                  onChange={e => setDraftName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveName(); }}
                  maxLength={80}
                  disabled={savingName}
                  placeholder="Story name"
                />
                <button className="btn-sm btn-accent" onClick={saveName} disabled={!draftName.trim() || savingName}>
                  {savingName ? (story ? 'Updating…' : 'Saving…') : (story ? 'Update' : 'Save')}
                </button>
              </span>
            ) : (
              <span onClick={() => setEditingName(true)} style={{ cursor: 'pointer' }}>{story?.name || 'Story'}</span>
            )}
            {story && saveState !== 'idle' && (
              <span className="se-save-state">{saveState === 'saving' ? 'Saving…' : 'Saved'}</span>
            )}
          </div>
        </div>

        {error && <div className="error-banner">⚠️ {error}</div>}
        
        {!story && !loadingStory && (
          <div className="info-banner">
            <strong>Name your Story</strong> — save a name in the breadcrumb above to start adding images.
          </div>
        )}

        {/* ═══ Filmstrip ═══ */}
        <div className="se-filmstrip-wrap" hidden={loadingStory}>
          <button className="se-filmstrip-arrow se-arrow-left" onClick={() => scrollFilmstrip('left')}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M8 1L3 6l5 5"/></svg>
          </button>
          <div className="se-filmstrip" ref={filmstripRef}>
            {slides.map((slide, i) => (
              <div
                key={i}
                className={`se-thumb ${i === activeIndex ? 'se-thumb-active' : ''}`}
                onClick={() => setActiveIndex(i)}
                draggable
                onDragStart={e => handleThumbDragStart(e, i)}
                onDragOver={handleThumbDragOver}
                onDragEnter={handleThumbDragEnter}
                onDragLeave={handleThumbDragLeave}
                onDrop={e => handleThumbDrop(e, i)}
                onDragEnd={handleThumbDragEnd}
              >
                {/* A captured poster is a still and always renders. Without one — a clip slide
                    reopened from the server — `preview` is the video itself, which an <img> can
                    only show as a broken icon, so let a muted video element draw the frame.
                    The fragment asks the browser to seek past any black leader. */}
                {slide.posterUrl || slide.sourceType !== 'video' ? (
                  <img src={slide.posterUrl || slide.preview} alt={`Slide ${i + 1}`} />
                ) : (
                  <video
                    src={`${slide.preview}#t=0.1`}
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                  />
                )}
                {slide.sourceType === 'video' && <span className="se-thumb-clip-badge" title="Clip slide">▶</span>}
                <div className="se-thumb-overlay">
                  <span className="se-thumb-num">{i + 1}</span>
                  <span className="se-thumb-name">{slide.fileName.length > 12 ? slide.fileName.slice(0, 10) + '…' : slide.fileName}</span>
                </div>
                <button className="se-thumb-delete" onClick={(e) => { e.stopPropagation(); removeSlide(i); }}>✕</button>
                {slide.caption && <div className="se-thumb-caption-dot" title={slide.caption}>T</div>}
              </div>
            ))}
            <button className="se-thumb se-thumb-add" onClick={() => fileInputRef.current?.click()}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              <span className="se-thumb-label">Add</span>
            </button>
          </div>
          <button className="se-filmstrip-arrow se-arrow-right" onClick={() => scrollFilmstrip('right')}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M4 1l5 5-5 5"/></svg>
          </button>
        </div>

        <input ref={fileInputRef} type="file" accept={`image/*,${VIDEO_ACCEPT}`} multiple style={{ display: 'none' }} onChange={handleSlideFileSelect} />
        <input ref={foregroundAudioInputRef} type="file" accept="audio/*" multiple style={{ display: 'none' }} onChange={addForegroundAudio} />
        <input ref={backgroundAudioInputRef} type="file" accept="audio/*" multiple style={{ display: 'none' }} onChange={addBackgroundAudio} />

        {/* ═══ Main Editor ═══ */}
        {loadingStory ? null : slides.length === 0 ? (
          <div className="project-empty">
            <div className="empty-icon">🖼</div>
            <h3>No slides yet</h3>
            <p>Add images or video clips to build your Story.</p>
            <button className="btn-primary" onClick={() => fileInputRef.current?.click()}>🖼 Add Images or Clips</button>
          </div>
        ) : activeSlide && (
          <div className="se-editor">
            {/* Preview */}
            <div className="se-preview-area">
              {watchingJob?.outputUrl ? (
                <div className="se-render-view">
                  <video src={watchingJob.outputUrl} controls autoPlay className="se-render-player" />
                  <div className="se-render-view-bar">
                    <button className="btn-sm" onClick={() => setWatchingJob(null)}>← Back to editing</button>
                    <a className="btn-sm" href={watchingJob.outputUrl} target="_blank" rel="noreferrer">⬇ Download</a>
                  </div>
                </div>
              ) : (() => {
                const displaySlide = previewing ? slides[previewSlideIndex] : activeSlide;
                if (!displaySlide) return null;
                return (
                  <>
                    <div
                      className="se-preview-frame"
                      ref={previewing ? undefined : previewRef}
                      style={{
                        aspectRatio: ASPECT_RATIOS.find(a => a.value === globalAspectRatio)?.css || '9 / 16',
                        height: globalAspectRatio === '16:9' ? 'auto' : '100%',
                        width: globalAspectRatio === '16:9' ? '100%' : 'auto',
                        maxWidth: '100%',
                        maxHeight: '100%',
                      }}
                    >
                      {displaySlide.sourceType === 'video' ? (
                        // Ken Burns is an image idea — a clip plays its own frames, trimmed.
                        <video
                          key={previewing ? previewSlideIndex : activeIndex}
                          ref={previewing ? undefined : clipVideoRef}
                          src={displaySlide.preview}
                          poster={displaySlide.posterUrl}
                          className="se-preview-img se-preview-video"
                          controls={!previewing}
                          autoPlay={previewing}
                          muted={displaySlide.sourceMuted || previewing}
                          playsInline
                          onLoadedMetadata={e => {
                            const el = e.currentTarget;
                            if (displaySlide.trimIn) el.currentTime = displaySlide.trimIn;
                          }}
                          onTimeUpdate={e => {
                            const el = e.currentTarget;
                            const out = displaySlide.trimOut;
                            if (out && el.currentTime >= out) {
                              el.currentTime = displaySlide.trimIn ?? 0;
                              if (!previewing) el.pause();
                            }
                          }}
                        />
                      ) : (
                        <img
                          key={previewing ? previewSlideIndex : activeIndex}
                          src={displaySlide.preview}
                          alt="Preview"
                          className={`se-preview-img${previewing ? ` se-motion-${getPreviewMotion(displaySlide, previewSlideIndex)}` : ''}`}
                          style={previewing ? { animationDuration: `${displaySlide.fixedDuration || 5}s` } : undefined}
                        />
                      )}
                      {/* Emoji overlays */}
                      {displaySlide.emojiOverlays.map(eo => (
                        <div
                          key={eo.emoji}
                          className="se-drag-overlay se-drag-emoji"
                          style={{ left: `${eo.x}%`, top: `${eo.y}%` }}
                          {...(!previewing ? { onMouseDown: (e: React.MouseEvent) => handleDragStart(e, 'emoji', eo.emoji, eo.x, eo.y) } : {})}
                        >
                          <span className="se-drag-content">{eo.emoji}</span>
                          {!previewing && <button className="se-drag-remove" onClick={(e) => { e.stopPropagation(); removeEmojiOverlay(eo.emoji); }}>✕</button>}
                        </div>
                      ))}
                      {/* Caption overlay */}
                      {displaySlide.captionOverlay && displaySlide.captionOverlay.text && (
                        <div
                          className={`se-drag-overlay se-drag-caption se-drag-align-${displaySlide.captionOverlay.align}`}
                          style={{
                            left: `${displaySlide.captionOverlay.x}%`,
                            top: `${displaySlide.captionOverlay.y}%`,
                            // Until the author resizes it, the plate hugs its text and cannot be
                            // too small for it — matching how the render draws an auto plate.
                            ...(displaySlide.captionOverlay.bgAuto === false
                              ? {
                                width: `${displaySlide.captionOverlay.bgWidth ?? DEFAULT_CAPTION_BOX.bgWidth}%`,
                                height: `${displaySlide.captionOverlay.bgHeight ?? DEFAULT_CAPTION_BOX.bgHeight}%`,
                              }
                              : {
                                width: 'max-content',
                                maxWidth: `${displaySlide.captionOverlay.bgWidth ?? DEFAULT_CAPTION_BOX.bgWidth}%`,
                                height: 'auto',
                              }),
                            ...textStyleToCss(displaySlide.captionOverlay, frameHeight),
                          }}
                          {...(!previewing ? { onMouseDown: (e: React.MouseEvent) => handleDragStart(e, 'caption', 'caption', displaySlide.captionOverlay!.x, displaySlide.captionOverlay!.y) } : {})}
                        >
                          {!previewing && (
                            <button
                              className="se-overlay-resize-handle"
                              onMouseDown={(e) => handleResizeStart(
                                e,
                                'caption',
                                displaySlide.captionOverlay!.bgWidth ?? DEFAULT_CAPTION_BOX.bgWidth,
                                displaySlide.captionOverlay!.bgHeight ?? DEFAULT_CAPTION_BOX.bgHeight,
                              )}
                              title="Resize caption background"
                            />
                          )}
                          {!previewing && <button className="se-drag-remove" onClick={(e) => { e.stopPropagation(); clearCaption(); }} title="Remove caption overlay">✕</button>}
                          <div className="se-overlay-text-body">
                            <span className="se-drag-content">{displaySlide.captionOverlay.text}</span>
                          </div>
                        </div>
                      )}
                      {displaySlide.showLongTextWithTts && displaySlide.ttsEnabled && displaySlide.longDescriptionText.trim().length > 0 && (() => {
                        const defaultNarrationBox = estimateNarrationBox(displaySlide.longDescriptionText);
                        const narrationOverlay = displaySlide.narrationOverlay || {
                          x: 50,
                          y: 68,
                          align: 'center' as CaptionAlign,
                          bgWidth: defaultNarrationBox.bgWidth,
                          bgHeight: defaultNarrationBox.bgHeight,
                        };
                        return (
                          <div
                            className={`se-drag-overlay se-drag-narration se-drag-align-${narrationOverlay.align}`}
                            style={{
                              left: `${narrationOverlay.x}%`,
                              top: `${narrationOverlay.y}%`,
                              width: `${narrationOverlay.bgWidth ?? DEFAULT_NARRATION_BOX.bgWidth}%`,
                              height: `${narrationOverlay.bgHeight ?? DEFAULT_NARRATION_BOX.bgHeight}%`,
                              ...textStyleToCss(narrationOverlay, frameHeight),
                            }}
                            {...(!previewing ? { onMouseDown: (e: React.MouseEvent) => handleDragStart(e, 'narration', 'narration', narrationOverlay.x, narrationOverlay.y) } : {})}
                          >
                            {!previewing && (
                              <button
                                className="se-overlay-resize-handle"
                                onMouseDown={(e) => handleResizeStart(
                                  e,
                                  'narration',
                                  narrationOverlay.bgWidth ?? DEFAULT_NARRATION_BOX.bgWidth,
                                  narrationOverlay.bgHeight ?? DEFAULT_NARRATION_BOX.bgHeight,
                                )}
                                title="Resize narration background"
                              />
                            )}
                            {!previewing && (
                              <button
                                className="se-drag-remove"
                                onClick={(e) => { e.stopPropagation(); clearNarrationOverlay(); }}
                                title="Remove narration overlay"
                              >
                                ✕
                              </button>
                            )}
                            <div className="se-overlay-text-body">
                              <span className="se-drag-content">{displaySlide.longDescriptionText}</span>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Slide counter during preview */}
                      {previewing && (
                        <div className="se-slideshow-counter">{previewSlideIndex + 1} / {slides.length}</div>
                      )}
                      {/* Hint when editing and nothing added */}
                      {!previewing && displaySlide.emojiOverlays.length === 0 && !displaySlide.captionOverlay?.text && !displaySlide.longDescriptionText && (
                        <div className="se-overlay-hint">
                          <p>Preview of changes made in right panel</p>
                          <p className="se-hint-sub">drag to reposition overlays</p>
                        </div>
                      )}
                    </div>
                    <div className="se-preview-info">
                      {previewing ? (
                        <span>Playing: Slide {previewSlideIndex + 1} of {slides.length}</span>
                      ) : (
                        <>
                          Slide {activeIndex + 1} of {slides.length}
                          {displaySlide.durationMode === 'fixed' && <span> · {displaySlide.fixedDuration}s</span>}
                          {displaySlide.durationMode === 'narration' && <span> · Match narration</span>}
                          {displaySlide.durationMode === 'audio' && <span> · Match audio</span>}
                        </>
                      )}
                      <span> · {globalAspectRatio}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            <RightPanel
              hasRenders={renderJobs.length > 0}
              rendersTab={story?.id ? (
                <RendersPanel
                  jobsUrl={`${API_BASE}/api/image-projects/${story.id}/jobs`}
                  labelFor={(job) => {
                    if (job.status === 'failed') return 'Render failed';
                    if (['done', 'completed'].includes(job.status)) return 'Story render';
                    return 'Rendering…';
                  }}
                  emptyText="No renders yet. Click Generate Story."
                  onJobsChange={setRenderJobs}
                  onRegenerate={() => {}}
                  onView={(job) => setWatchingJob(job)}
                  selectedJobId={watchingJob?.id || null}
                />
              ) : undefined}
              slide={activeSlide}
              slideIndex={activeIndex}
              slideCount={slides.length}
              durationIsFixed={isDurationFixed()}
              enabledKBSteps={enabledKBSteps}
              totalKBDuration={totalKBDuration}
              audioLibrary={audioLibrary}
              loadingLibrary={loadingLibrary}
              updateActive={updateActive}
              setCaptionText={setCaptionText}
              setCaptionAlign={setCaptionAlign}
              setCaptionStyle={setCaptionStyle}
              clearCaption={clearCaption}
              updateNarrationText={updateNarrationText}
              setNarrationAlign={setNarrationAlign}
              setNarrationStyle={setNarrationStyle}
              toggleKBEffect={toggleKBEffect}
              updateKBDuration={updateKBDuration}
              addLibraryAudio={addLibraryAudio}
              removeAudio={removeAudio}
              onUploadForeground={() => foregroundAudioInputRef.current?.click()}
              onUploadBackground={() => backgroundAudioInputRef.current?.click()}
              onOpenClipEditor={openClipEditor}
              openingClip={openingClip}
              description={description}
              onDescriptionChange={setDescription}
              aspectRatio={globalAspectRatio}
              onAspectRatioChange={setGlobalAspectRatio}
              platform={platform}
              onPlatformChange={setPlatform}
              audience={audience}
              onAudienceChange={setAudience}
            />
          </div>
        )}

        {/* ═══ Bottom Bar ═══ */}
        {slides.length > 0 && (
          <div className="se-bottom-bar">
            <div className="se-bottom-settings">
              {/* Platform and audience are Story-scoped — they live in the Story tab. */}
              <span className="se-slide-count">
                {slides.length} slide{slides.length !== 1 ? 's' : ''}
                {clipSlideCount > 0 && ` · ${clipSlideCount} clip${clipSlideCount !== 1 ? 's' : ''}`}
              </span>
            </div>
            <div className="se-bottom-actions">
              <button
                className="se-btn-preview"
                onClick={previewing ? stopPreview : startPreview}
                disabled={submitting || slides.length === 0}
              >
                {previewing ? (
                  <><svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="2" y="2" width="10" height="10" rx="1"/></svg> Stop</>
                ) : (
                  <><svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5l9 5.5-9 5.5z"/></svg> Preview</>
                )}
              </button>
              <button
                className="se-btn-generate"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <><span className="se-spinner"></span> Generating…</>
                ) : (
                  <><svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="12" height="10" rx="2"/><path d="M6.5 6.5l3 1.5-3 1.5z" fill="currentColor"/></svg> Generate Story</>
                )}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ImageVideoUpload;
